import type { FoLayoutProps } from "@/modules/layouts/components/FoLayout"
import type { FunctionComponent } from "react"

import { useParams } from "next/navigation"
import { SkeletonTheme } from "react-loading-skeleton"

import { MAIN_THEME } from "@jobteaser/ui-kit/constants/skeleton"

import { Body } from "@/modules/eventDetails/client/components/Body/Body"
import { Breadcrumb } from "@/modules/eventDetails/client/components/Breadcrumb/Breadcrumb"
import { Header } from "@/modules/eventDetails/client/components/Header/Header"
import { MainDetails } from "@/modules/eventDetails/client/components/MainDetails/MainDetails"
import { RegistrationFormProvider } from "@/modules/eventDetails/client/Context/RegistrationFormContext"
import { useEventDetailsData } from "@/modules/eventDetails/client/hooks/useEventDetailsData"
import { FoLayout } from "@/modules/layouts/components/FoLayout"
import { MetaTags } from "@/modules/MetaTags/MetaTags"

import "react-loading-skeleton/dist/skeleton.css"
import styles from "./Layout.module.css"

export type PageLayoutProps = {
  host: FoLayoutProps["host"]
}

export const PageLayout: FunctionComponent<PageLayoutProps> = ({ host }) => {
  const { eventSlug } = useParams<{ eventSlug: string }>()
  const event = useEventDetailsData(eventSlug)

  if (!event) {
    return null
  }

  const MAX_META_DESCRIPTION_LENGTH = 300
  // transform html to text
  const textDescription = event.description.replace(/<[^>]+>/g, "")
  // trim to the description length if needed
  const metaDescription =
    textDescription.length > MAX_META_DESCRIPTION_LENGTH
      ? `${textDescription.slice(0, MAX_META_DESCRIPTION_LENGTH)}...`
      : textDescription

  return (
    <RegistrationFormProvider>
      <MetaTags title={event.title} description={metaDescription} image={event.coverUrl} />
      <FoLayout host={host} className={styles.foLayout}>
        <SkeletonTheme {...MAIN_THEME}>
          <div className={styles.wrapper}>
            <Breadcrumb />
            <Header />
            <aside className={styles.aside}>
              <MainDetails />
            </aside>
            <Body />
          </div>
        </SkeletonTheme>
      </FoLayout>
    </RegistrationFormProvider>
  )
}
